import { useState, useEffect, useRef } from 'react';
import { HowItWorksCard } from './HowItWorksCard';
import { HandshakeIcon, Play, Sparkles, UserPlus, MessageCircle, TrendingUp, ArrowRight } from 'lucide-react';
import { trackButtonClick } from '../../firebaseConfig';
import { DemoModal } from '../Hero/DemoModal';
import { preloadThemeVideos } from '../../lib/videoPreloader';
import { getOptimizedVideoUrl, getOptimizedImageUrl } from '../../lib/optimizedAssets';
import { useNavigate } from 'react-router-dom';
import { useAnalyticsTracking } from '../../hooks/useAnalyticsTracking';

const steps = [
  {
    step: 1,
    gifDark: getOptimizedVideoUrl('boarding-dark'),
    gifLight: getOptimizedVideoUrl('boarding-light'),
    videoKey: 'boarding',
    title: "Sign Up & Profile Setup",
    description: "Setup your account in MockTrail using JobProt, it manages everything for you. Your Resumes, Job Preferences and more.",
    subtext: "JobProt simplifies the process of setting up your account & AI-powered resumes."
  },
  {
    step: 2,
    gifDark: getOptimizedVideoUrl('choose-dark'),
    gifLight: getOptimizedVideoUrl('choose-light'),
    videoKey: 'choose',
    title: "Choose an Interview",
    description: "Choose an interview from our practice interview & real life company interview rounds personalized for you.",
    subtext: "You get to choose the company & role you want to practice for."
  },
  {
    step: 3,
    gifDark: getOptimizedVideoUrl('startint-dark'),
    gifLight: getOptimizedVideoUrl('startint-light'),
    videoKey: 'startint',
    title: "Start Interviewing",
    description: "Practice with our AI interviewer, that listens you, sees you and asks you related questions.",
    subtext: "Our AI Interivewer listens you, sees your video camera and even screen. "
  },
  {
    step: 4,
    gifDark: getOptimizedVideoUrl('feedbpk-dark'),
    gifLight: getOptimizedVideoUrl('feedbpk-light'),
    videoKey: 'feedbpk',
    title: "Get Detailed Feedback",
    description: "Our AI Interviewer captures & analyzes everything, and gives you detailed feedback on various aspects of the interview.",
    subtext: "You can see your score, impression over the interview, areas of improvement, and more."
  }
];

export function HowItWorks() {
  const [isDemoOpen, setIsDemoOpen] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [animationPhase, setAnimationPhase] = useState(0);
  const sectionRef = useRef<HTMLElement>(null);
  const [visibleCards, setVisibleCards] = useState<number[]>([]);
  const [isDark, setIsDark] = useState(true);
  const navigate = useNavigate();
  const { trackButtonClick: trackAnalyticsButton, trackConversion, trackSectionView, trackNavigation } = useAnalyticsTracking();

  // Detect theme change and initialize
  useEffect(() => {
    // Check for dark mode
    const checkDarkMode = () => {
      const isDarkMode = document.documentElement.classList.contains('dark');
      setIsDark(isDarkMode);
    };

    // Initial check
    checkDarkMode();

    // Set up listeners for changes
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (
          mutation.attributeName === 'class' &&
          mutation.target === document.documentElement
        ) {
          checkDarkMode();
        }
      });
    });

    // Start observing document for class changes
    observer.observe(document.documentElement, { attributes: true });

    return () => {
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // Track section view
            trackSectionView('How It Works Section', {
              section_name: 'how_it_works',
              section_position: 'middle',
              visibility_percentage: Math.round(entry.intersectionRatio * 100)
            });
            
            entry.target.classList.add('animate-fade-in');
          }
        });
      },
      { threshold: 0.1, rootMargin: '50px' }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, [trackSectionView]);

  const handleSignUpNow = () => {
    // Comprehensive analytics tracking
    trackAnalyticsButton('Sign Up Now', 'How It Works Section', {
      button_type: 'primary_cta',
      button_position: 'how_it_works_main',
      conversion_intent: 'sign_up',
      utm_source: 'how_it_works_section',
      utm_medium: 'cta_button',
      utm_campaign: 'sign_up_now'
    });
    
    // Track conversion
    trackConversion('sign_up', {
      source: 'how_it_works_section',
      button_name: 'Sign Up Now'
    });
    
    // Keep existing Firebase tracking for backward compatibility
    trackButtonClick('Sign Up Now', 'How It Works');
    
    const platformUrl = import.meta.env.VITE_MOCKTRAIL_PLATFORM_URL;
    window.open(`${platformUrl}/login?utm_source=how_it_works_section&utm_medium=cta_button&utm_campaign=sign_up_now`, '_blank');
  };

  const handleBecomePartner = () => {
    // Comprehensive analytics tracking
    trackAnalyticsButton('Become a Partner', 'How It Works Section', {
      button_type: 'secondary_cta',
      button_position: 'how_it_works_partner',
      conversion_intent: 'partner_program',
      utm_source: 'how_it_works_section',
      utm_medium: 'cta_button',
      utm_campaign: 'become_partner'
    });
    
    // Track navigation
    trackNavigation('Ambassador Program', {
      source: 'how_it_works_section',
      navigation_type: 'internal_link'
    });
    
    // Keep existing Firebase tracking for backward compatibility
    trackButtonClick('Become a Partner', 'How It Works');
    
    navigate('/ambassador-program');
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);

          // Preload videos for current theme
          const videoUrls = steps.map(step => ({
            dark: step.gifDark,
            light: step.gifLight
          }));
          preloadThemeVideos(videoUrls, isDark);

          // Staggered animation sequence
          setTimeout(() => setAnimationPhase(1), 100); // Section title
          setTimeout(() => setAnimationPhase(2), 300); // Description
          setTimeout(() => {
            // Sequentially reveal each step card
            const revealCards = () => {
              steps.forEach((_, index) => {
                setTimeout(() => {
                  setVisibleCards(prev => [...prev, index]);
                }, index * 300); // 300ms delay between each card
              });
            };
            revealCards();
          }, 600);
          setTimeout(() => setAnimationPhase(3), 600 + steps.length * 300 + 200); // CTA Button

          observer.disconnect();
        }
      },
      { threshold: 0.1 } // Balanced trigger for preloading
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, [isDark]);

  return (
    <section
      ref={sectionRef}
      id="how-it-works"
      className="py-32 relative overflow-hidden"
    >
      {/* Enhanced Background Effects */}
      <div
        className="absolute inset-0 bg-gradient-to-b from-transparent via-primary-light/5 to-transparent dark:via-primary-dark/5 opacity-0 transition-opacity duration-1000"
        style={{ opacity: isVisible ? 1 : 0 }}
      />
      <div
        className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,rgba(37,99,235,0.05),transparent_50%)] dark:bg-[radial-gradient(circle_at_30%_50%,rgba(59,130,246,0.05),transparent_50%)] parallax-element opacity-0 transition-opacity duration-1000 delay-200"
        data-speed="0.05"
        style={{ opacity: isVisible ? 1 : 0 }}
      />
      <div
        className="absolute inset-0 bg-[radial-gradient(circle_at_70%_50%,rgba(16,185,129,0.05),transparent_50%)] dark:bg-[radial-gradient(circle_at_70%_50%,rgba(52,211,153,0.05),transparent_50%)] parallax-element opacity-0 transition-opacity duration-1000 delay-300"
        data-speed="0.08"
        style={{ opacity: isVisible ? 1 : 0 }}
      />

      {/* Floating elements */}
      <div
        className="absolute left-[15%] top-[25%] w-5 h-5 rounded-full bg-primary-light/15 dark:bg-primary-dark/15 blur-md animate-float parallax-element opacity-0 transition-opacity duration-1000 delay-400"
        data-speed="0.14"
        style={{ opacity: isVisible ? 0.7 : 0, animationDelay: '0.3s', animationDuration: '7s' }}
      />
      <div
        className="absolute right-[20%] top-[35%] w-4 h-4 rounded-full bg-secondary-light/15 dark:bg-secondary-dark/15 blur-md animate-float parallax-element opacity-0 transition-opacity duration-1000 delay-600"
        data-speed="0.11"
        style={{ opacity: isVisible ? 0.6 : 0, animationDelay: '0.8s', animationDuration: '6s' }}
      />

      {/* Removed the number line connection */}

      <div className="container mx-auto px-4 relative">
        <div className="text-center mb-20">
          <h2
            className={`text-3xl md:text-4xl font-bold mb-6 text-text-primary-light dark:text-text-primary-dark transform transition-all duration-1000 ease-[cubic-bezier(0.16,1,0.3,1)] ${animationPhase >= 1 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
          >
            How MockTrail Works
          </h2>
          <p
            className={`text-lg text-text-secondary-light dark:text-text-secondary-dark max-w-2xl mx-auto transform transition-all duration-1000 ease-[cubic-bezier(0.16,1,0.3,1)] ${animationPhase >= 2 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
              }`}
          >
            Start prepping for your next interview in minutes. Upload, practice & start improving in few steps.
          </p>
        </div>

        <div className="space-y-6 max-w-6xl mx-auto">
          {steps.map((step, index) => (
            <div
              key={index}
              className={`transform transition-all duration-1000 ease-[cubic-bezier(0.16,1,0.3,1)] ${visibleCards.includes(index)
                  ? 'opacity-100 translate-y-0'
                  : 'opacity-0 translate-y-12'
                }`}
              style={{ transitionDelay: `${index * 0.1}s` }}
            >
              <HowItWorksCard {...step} isDark={isDark} />
            </div>
          ))}
        </div>

        {/* CTA Button */}
        <div
          className={`mt-16 text-center transform gap-4 flex flex-col sm:flex-row justify-center items-center transition-all duration-1000 ease-[cubic-bezier(0.16,1,0.3,1)] ${animationPhase >= 3 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
        >
          <button
            onClick={handleSignUpNow}
            className="btn-primary w-full sm:w-auto inline-flex items-center justify-center gap-2 py-4 text-lg relative overflow-hidden group"
          >
            {/* Button shine effect */}
            <span className="absolute inset-0 w-full h-full bg-gradient-to-r from-transparent via-white to-transparent opacity-0 group-hover:opacity-20 -translate-x-full group-hover:translate-x-full transition-all duration-1000 ease-out"></span>

            <UserPlus className="w-5 h-5" />
            Sign Up Now
          </button>
          <button
            onClick={handleBecomePartner}
            className="btn-secondary inline-flex items-center justify-center gap-2 w-full sm:w-auto"
          >
            <HandshakeIcon className="w-5 h-5" />
            Become a Partner
          </button>
        </div>
      </div>
      {isDemoOpen && <DemoModal onClose={() => setIsDemoOpen(false)} isOpen={isDemoOpen} />}
    </section>
  );
}